import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import MobileHeader from "../components/MobileHeader";
import { supabase } from "../supabase";
import { jsPDF } from "jspdf";

const BOT_TOKEN = "8628392776:AAEIkaTzeyZNhWz7IY0ZCzrNGPs-hJcsweg";

const getExpiryStatus = (dateStr) => {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [y, m, d] = String(dateStr).split("-");
  const expiry = new Date(Number(y), Number(m) - 1, Number(d));
  const daysLeft = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  if (daysLeft < 0)
    return { label: "Expired", color: "#a32d2d", bg: "#fcebeb" };
  if (daysLeft <= 30)
    return {
      label: `Expires in ${daysLeft}d`,
      color: "#854f0b",
      bg: "#faeeda",
    };
  return {
    label: `Valid · ${daysLeft}d left`,
    color: "#3b6d11",
    bg: "#eaf3de",
  };
};

const fmt = (n) =>
  `$${parseFloat(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const fmtDate = (d, opts) => {
  if (!d) return "";
  const [y, m, day] = String(d).split("-");
  return new Date(Number(y), Number(m) - 1, Number(day)).toLocaleDateString(
    "en-US",
    opts || {}
  );
};

const fmtDateDisplay = (d) => {
  if (!d) return "N/A";
  const [y, m, day] = String(d).split("-");
  return `${String(m).padStart(2, "0")}/${String(day).padStart(2, "0")}/${y}`;
};

// ── Doc tile pill ──
const DocPill = ({ onFile }) =>
  onFile ? (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        fontSize: "10px",
        fontWeight: "500",
        padding: "3px 8px",
        borderRadius: "20px",
        background: "rgba(61,204,130,0.12)",
        color: "#3dcc82",
      }}
    >
      <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="20 6 9 17 4 12" />
      </svg>
      On File
    </div>
  ) : (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        fontSize: "10px",
        fontWeight: "500",
        padding: "3px 8px",
        borderRadius: "20px",
        background: "rgba(255,255,255,0.05)",
        color: "rgba(255,255,255,0.3)",
      }}
    >
      <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
      </svg>
      Not on File
    </div>
  );

// ── Upload area ──
const UploadArea = ({ label, onUpload, uploading }) => (
  <label
    style={{
      display: "block",
      border: "1.5px dashed #e0e0e0",
      borderRadius: "8px",
      padding: "20px",
      textAlign: "center",
      cursor: "pointer",
      transition: "all 0.15s",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.borderColor = "#3a9e5f";
      e.currentTarget.style.background = "#f0faf4";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.borderColor = "#e0e0e0";
      e.currentTarget.style.background = "transparent";
    }}
  >
    <input
      type="file"
      accept="image/*,.pdf"
      style={{ display: "none" }}
      onChange={onUpload}
    />
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#9ca3af"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block", margin: "0 auto 6px" }}
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
    <div style={{ fontSize: "12px", color: "#9ca3af" }}>
      {uploading ? "Uploading..." : `Click to upload ${label}`}
    </div>
    <div style={{ fontSize: "10px", color: "#c0c0c0", marginTop: "2px" }}>
      JPG, PNG or PDF · max 10MB
    </div>
  </label>
);

// ── Doc image preview ──
const DocPreview = ({ url, onReplace, replacing, onPreview }) => {
  const isPdf = url?.toLowerCase().includes(".pdf");
  const isGoogleDrive = url?.includes("drive.google.com");
  return (
    <div>
      <div
        style={{
          background: "#f8f8f8",
          border: "0.5px solid #e8e8e8",
          borderRadius: "8px",
          overflow: "hidden",
          marginBottom: "6px",
        }}
      >
        {isGoogleDrive ? (
          <div style={{ padding: "20px", textAlign: "center" }}>
            <div style={{ fontSize: "28px", marginBottom: "6px" }}>📄</div>
            <div
              style={{
                fontSize: "11px",
                color: "#9ca3af",
                marginBottom: "8px",
              }}
            >
              Stored on Google Drive
            </div>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              style={{
                fontSize: "12px",
                color: "#3a9e5f",
                textDecoration: "none",
              }}
            >
              View on Google Drive →
            </a>
          </div>
        ) : isPdf ? (
          <div style={{ padding: "20px", textAlign: "center" }}>
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#9ca3af"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ display: "block", margin: "0 auto 6px" }}
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              style={{
                fontSize: "12px",
                color: "#3a9e5f",
                textDecoration: "none",
              }}
            >
              View PDF
            </a>
          </div>
        ) : (
          <div
            style={{ position: "relative", cursor: "zoom-in" }}
            onClick={() => onPreview && onPreview(url)}
          >
            <img
              src={url}
              alt="Document"
              style={{
                width: "100%",
                maxHeight: "160px",
                objectFit: "contain",
                display: "block",
              }}
            />
            <div
              style={{
                position: "absolute",
                bottom: "6px",
                right: "6px",
                background: "rgba(0,0,0,0.5)",
                borderRadius: "4px",
                padding: "3px 6px",
                fontSize: "10px",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                gap: "3px",
              }}
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                <line x1="11" y1="8" x2="11" y2="14" />
                <line x1="8" y1="11" x2="14" y2="11" />
              </svg>
              Preview
            </div>
          </div>
        )}
      </div>
      <label
        style={{
          display: "block",
          background: "#f5f5f5",
          border: "0.5px solid #e8e8e8",
          borderRadius: "7px",
          padding: "6px",
          fontSize: "11px",
          color: "#6b7280",
          cursor: "pointer",
          textAlign: "center",
        }}
      >
        <input
          type="file"
          accept="image/*,.pdf"
          style={{ display: "none" }}
          onChange={onReplace}
        />
        {replacing ? "Uploading..." : "↑ Replace image"}
      </label>
    </div>
  );
};

export default function DriverProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [logoImg, setLogoImg] = useState(null);
  const [statements, setStatements] = useState([]);
  const [loadingStatements, setLoadingStatements] = useState(true);
  const [assignedTruck, setAssignedTruck] = useState(null);

  // ── Doc panel state ──
  const [activeDoc, setActiveDoc] = useState(null); // 'cdl' | 'med' | 'mvr' | 'ch' | 'ssn'
  const [docForm, setDocForm] = useState({});
  const [docSaving, setDocSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  useEffect(() => {
    fetchDriver();
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    const img = new Image();
    img.src = "/Logo_Porso_Vertical-01.png";
    img.onload = () => setLogoImg(img);
    return () => window.removeEventListener("resize", handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchDriver = async () => {
    const { data } = await supabase
      .from("drivers")
      .select("*")
      .eq("id", id)
      .single();
    setDriver(data);
    setEditData(data || {});
    setNote(data?.notes || "");
    setLoading(false);
    if (data) {
      fetchStatements(data.id);
      fetchAssignedTruck(data.id);
    }
  };
  const fetchAssignedTruck = async (driverId) => {
    const { data } = await supabase
      .from("trucks")
      .select(
        "id, unit_number, make, year, license_plate, state, vin, ownership"
      )
      .contains("assigned_drivers", [driverId])
      .eq("status", "active")
      .maybeSingle();
    setAssignedTruck(data || null);
  };

  const fetchStatements = async (driverId) => {
    setLoadingStatements(true);
    const { data } = await supabase
      .from("statements")
      .select("*")
      .eq("driver_id", driverId)
      .order("period_end", { ascending: false });
    setStatements(data || []);
    setLoadingStatements(false);
  };

  const totalNetPaid = statements.reduce(
    (s, st) => s + parseFloat(st.net_pay || 0),
    0
  );

  const openDocPanel = (docKey) => {
    setActiveDoc(docKey);
    if (!driver) return;
    const forms = {
      cdl: {
        cdl_number: driver.cdl_number || "",
        cdl_issued_state: driver.cdl_issued_state || "",
        cdl_class: driver.cdl_class || "",
        cdl_issue_date: driver.cdl_issue_date || "",
        cdl_expiry: driver.cdl_expiry || "",
        cdl_notes: driver.cdl_notes || "",
      },
      med: {
        med_card_expiry: driver.med_card_expiry || "",
        med_card_notes: driver.med_card_notes || "",
      },
      mvr: {
        mvr_collected_on: driver.mvr_collected_on || "",
        mvr_expiry: driver.mvr_expiry || "",
        mvr_notes: driver.mvr_notes || "",
      },
      ch: {
        clearinghouse_collected_on: driver.clearinghouse_collected_on || "",
        clearinghouse_expiry: driver.clearinghouse_expiry || "",
        clearinghouse_notes: driver.clearinghouse_notes || "",
      },
      ssn: { ssn: driver.ssn || "", ssn_notes: driver.ssn_notes || "" },
    };
    setDocForm(forms[docKey] || {});
  };

  const closeDocPanel = () => {
    setActiveDoc(null);
    setDocForm({});
  };

  const cancelDocPanel = () => {
    openDocPanel(activeDoc);
  }; // reset form to driver values

  const saveDocPanel = async () => {
    setDocSaving(true);
    // Convert empty strings to null so date columns don't fail
    const payload = Object.fromEntries(
      Object.entries(docForm).map(([k, v]) => [k, v === "" ? null : v])
    );
    const { error } = await supabase
      .from("drivers")
      .update(payload)
      .eq("id", id);
    if (!error) {
      setDriver((prev) => ({ ...prev, ...payload }));
    } else {
      console.error("saveDocPanel error:", error);
      alert("Save failed: " + error.message);
    }
    setDocSaving(false);
  };

  const uploadDoc = async (file, bucket, column) => {
    if (!file) return;
    setUploading(true);
    const ext = file.name.split(".").pop();
    const fileName = `${id}_${column}_${Date.now()}.${ext}`;
    const { error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(fileName, file, { upsert: true });
    if (!uploadError) {
      const { data: urlData } = supabase.storage
        .from(bucket)
        .getPublicUrl(fileName);
      await supabase
        .from("drivers")
        .update({ [column]: urlData.publicUrl })
        .eq("id", id);
      setDriver((prev) => ({ ...prev, [column]: urlData.publicUrl }));
    }
    setUploading(false);
  };

  const sendTelegramMessage = async (chatId, message) => {
    await fetch(`https://api.telegram.org/bot${BOT_TOKEN}/sendMessage`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        chat_id: chatId,
        text: message,
        parse_mode: "Markdown",
      }),
    });
  };

  const updateStatus = async (field, value) => {
    setUpdating(field);
    await supabase
      .from("drivers")
      .update({ [field]: value })
      .eq("id", id);
    const messages = {
      drug_test: {
        pass: `✅ *Good news ${driver.first_name}!*\n\nYour drug test came back *PASSED*. 🎉\n\nWe will now proceed with your CDL history check.\n\n_Porso Freight Team 🚛_`,
        fail: `❌ *Hi ${driver.first_name},*\n\nUnfortunately your drug test did not meet our requirements.\n\nThank you for your interest in Porso Freight.\n\n_Porso Freight Team 🚛_`,
      },
      cdl_history: {
        pass: `✅ *Great news ${driver.first_name}!*\n\nYour CDL history check came back *PASSED*. 🎉\n\nWe will be in touch shortly.\n\n_Porso Freight Team 🚛_`,
        fail: `❌ *Hi ${driver.first_name},*\n\nUnfortunately your CDL history check did not meet our requirements.\n\nThank you for your interest in Porso Freight.\n\n_Porso Freight Team 🚛_`,
      },
      contract_sent: {
        sent: `📝 *Hi ${driver.first_name}!*\n\nCongratulations! You have passed all requirements. 🎉\n\nYour contract is being prepared and our team will be in touch shortly.\n\n_Porso Freight Team 🚛_`,
      },
    };
    if (driver.chat_id && messages[field]?.[value])
      await sendTelegramMessage(driver.chat_id, messages[field][value]);
    setDriver((prev) => ({ ...prev, [field]: value }));
    setUpdating("");
  };

  const saveEdit = async () => {
    setSaving(true);
    await supabase
      .from("drivers")
      .update({
        first_name: editData.first_name,
        last_name: editData.last_name,
        dob: editData.dob,
        ssn: editData.ssn,
        phone: editData.phone,
        email: editData.email,
        address: editData.address,
        has_corp: editData.has_corp,
        corp_name: editData.corp_name,
        corp_address: editData.corp_address,
        ein: editData.ein,
        routing: editData.routing,
        account: editData.account,
        unit_number: editData.unit_number,
        vin: editData.vin,
        truck_year: editData.truck_year,
        truck_make: editData.truck_make,
        cdl_expiry: editData.cdl_expiry,
        med_card_expiry: editData.med_card_expiry,
        chat_id: editData.chat_id ? String(editData.chat_id).trim() : null,
        pp_device_id: editData.pp_device_id || null,
      })
      .eq("id", id);
    setDriver({ ...driver, ...editData });
    setEditing(false);
    setSaving(false);
  };

  const saveNote = async () => {
    setSavingNote(true);
    await supabase.from("drivers").update({ notes: note }).eq("id", id);
    setDriver((prev) => ({ ...prev, notes: note }));
    setSavingNote(false);
  };

  const deleteDriver = async () => {
    await supabase.from("drivers").delete().eq("id", id);
    navigate("/drivers");
  };

  const generateContract = () => {
    const doc = new jsPDF();
    const today = new Date().toLocaleDateString("en-US");
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - margin * 2;
    const centerText = (text, y, size = 12, style = "normal") => {
      doc.setFont("helvetica", style);
      doc.setFontSize(size);
      doc.text(text, pageWidth / 2, y, { align: "center" });
    };
    const leftText = (text, y, size = 10, style = "normal") => {
      doc.setFont("helvetica", style);
      doc.setFontSize(size);
      doc.text(text, margin, y);
    };
    const drawLine = (y, color = 180) => {
      doc.setDrawColor(color);
      doc.line(margin, y, pageWidth - margin, y);
    };
    const ownerName =
      driver.corp_name ||
      `${driver.first_name || ""} ${driver.last_name || ""}`.trim() ||
      "EQUIPMENT OWNER";
    const ownerAddress =
      driver.corp_address || driver.address || "EQUIPMENT OWNER ADDRESS";
    const vin = (driver.vin || "").trim().toUpperCase();
    const unitNum =
      driver.unit_number || (vin.length >= 4 ? vin.slice(-4) : "—");
    const truckDesc =
      `${driver.truck_year || ""} ${driver.truck_make || ""} ${vin}`.trim() ||
      "—";
    if (logoImg) doc.addImage(logoImg, "PNG", margin, 8, 60, 20);
    centerText("LEASE AGREEMENT", 36, 16, "bold");
    drawLine(41);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    const noticeLines = doc.splitTextToSize(
      "Notice: This Lease Agreement must be retained in the Equipment throughout the duration of the Agreement.",
      contentWidth
    );
    doc.text(noticeLines, pageWidth / 2, 48, { align: "center" });
    drawLine(54);
    leftText("I.", 62, 10, "bold");
    doc.setFontSize(9);
    let ix = margin + 8;
    let iy = 62;
    const writeInline = (text, style = "normal") => {
      doc.setFont("helvetica", style);
      text.split(" ").forEach((word, i, arr) => {
        const w = word + (i < arr.length - 1 ? " " : "");
        const ww = doc.getTextWidth(w);
        if (ix + ww > pageWidth - margin) {
          iy += 6;
          ix = margin + 8;
        }
        doc.text(w, ix, iy);
        ix += ww;
      });
    };
    [
      { text: "This Agreement is between ", style: "normal" },
      { text: ownerName, style: "bold" },
      {
        text: `, with a registered address at ${ownerAddress} and `,
        style: "normal",
      },
      { text: "PORSO FREIGHT LLC", style: "bold" },
      {
        text:
          ", with a registered address at 121 MILTON ST DAYTON OH 45403 are parties to a written Lease Agreement (Agreement), whereby the Equipment Owner has leased to the Carrier certain motor vehicle equipment listed below, owned and controlled by the Equipment Owner, whereby the Equipment Owner is providing the Carrier as operator of the Equipment for the purpose of loading, transporting and unloading freight. Carrier is responsible for all IFTA filing and reporting.",
        style: "normal",
      },
    ].forEach((p) => writeInline(p.text, p.style));
    const afterI = iy + 10;
    drawLine(afterI);
    const secII_y = afterI + 8;
    leftText("II.", secII_y, 10, "bold");
    doc.setFontSize(9);
    let iix = margin + 8;
    let iiy = secII_y;
    const writeInline2 = (text, style = "normal") => {
      doc.setFont("helvetica", style);
      text.split(" ").forEach((word, i, arr) => {
        const w = word + (i < arr.length - 1 ? " " : "");
        const ww = doc.getTextWidth(w);
        if (iix + ww > pageWidth - margin) {
          iiy += 6;
          iix = margin + 8;
        }
        doc.text(w, iix, iiy);
        iix += ww;
      });
    };
    writeInline2(
      "The Original Agreement is on file at the Carrier's General Office. A copy of this Lease Agreement and receipt for the Equipment must be carried on the Equipment as required by ",
      "normal"
    );
    writeInline2("49 CFR 376", "bold");
    writeInline2(
      ". Carrier verifies that the Equipment is being operated by the Carrier, pursuant to the terms of the Agreement.",
      "normal"
    );
    const afterII = iiy + 10;
    drawLine(afterII);
    const eqHdr_y = afterII + 7;
    leftText(
      "III.  Equipment Owner / Equipment Information",
      eqHdr_y,
      11,
      "bold"
    );
    drawLine(eqHdr_y + 4);
    const tableTop = eqHdr_y + 7;
    const tableH = 22;
    const col2x = margin + 55;
    const col3x = margin + 100;
    doc.setDrawColor(150);
    doc.rect(margin, tableTop, contentWidth, tableH);
    doc.line(col2x, tableTop, col2x, tableTop + tableH);
    doc.line(col3x, tableTop, col3x, tableTop + tableH);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("Name", margin + 3, tableTop + 7);
    doc.text("Unit #", col2x + 3, tableTop + 7);
    doc.text("Year / Make / VIN", col3x + 3, tableTop + 7);
    doc.line(margin, tableTop + 10, margin + contentWidth, tableTop + 10);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(ownerName, margin + 3, tableTop + 18);
    doc.text(unitNum, col2x + 3, tableTop + 18);
    doc.text(
      doc.splitTextToSize(truckDesc, contentWidth - 102),
      col3x + 3,
      tableTop + 16
    );
    const afterTable = tableTop + tableH + 5;
    drawLine(afterTable);
    const dur_y = afterTable + 7;
    leftText("Duration of Lease Agreement and Termination", dur_y, 11, "bold");
    drawLine(dur_y + 4);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const splitDur = doc.splitTextToSize(
      "The Lease Agreement shall begin on the date of signing and shall remain in effect until terminated by either party, giving one (1) day notice to that effect. Notice may be given personally, by mail or by fax at the address or fax number shown in the Lease Agreement.",
      contentWidth
    );
    doc.text(splitDur, margin, dur_y + 9, { lineHeightFactor: 1.5 });
    const afterDur = dur_y + 9 + splitDur.length * 6 + 3;
    drawLine(afterDur);
    const date_y = afterDur + 8;
    leftText(`Signed this date:  ${today}`, date_y, 10, "normal");
    drawLine(date_y + 10);
    const sig_y = date_y + 18;
    const col_R = pageWidth / 2 + 5;
    leftText("MOTOR CARRIER:", sig_y, 10, "bold");
    leftText("PORSO FREIGHT LLC", sig_y + 7, 10, "normal");
    leftText("Akbar Alijon", sig_y + 14, 9, "bold");
    drawLine(sig_y + 22);
    leftText("By:", sig_y + 27, 9, "normal");
    leftText("Akbar Alijon", sig_y + 33, 9, "normal");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("EQUIPMENT OWNER:", col_R, sig_y);
    doc.setFont("helvetica", "normal");
    doc.text(ownerName, col_R, sig_y + 7);
    doc.line(col_R, sig_y + 22, pageWidth - margin, sig_y + 22);
    doc.setFontSize(9);
    doc.text("By:", col_R, sig_y + 27);
    window.open(URL.createObjectURL(doc.output("blob")), "_blank");
  };

  const getBadge = (status) => {
    const styles = {
      pass: "bg-green-100 text-green-800",
      fail: "bg-red-100 text-red-800",
      pending: "bg-amber-100 text-amber-800",
      sent: "bg-blue-100 text-blue-800",
    };
    return `t-badge px-2 py-0.5 rounded-full ${
      styles[status] || styles.pending
    }`;
  };

  const inputStyle = {
    width: "100%",
    border: "0.5px solid #e0e0e0",
    borderRadius: "6px",
    padding: "5px 8px",
    fontSize: "13px",
    outline: "none",
    boxSizing: "border-box",
  };
  const panelInputStyle = {
    width: "100%",
    border: "0.5px solid #e0e0e0",
    borderRadius: "7px",
    padding: "7px 10px",
    fontSize: "12px",
    outline: "none",
    boxSizing: "border-box",
    color: "#111",
    fontFamily: "inherit",
  };

  const editField = (key, label, type = "text") => (
    <div key={key}>
      <div className="t-label" style={{ marginBottom: "4px" }}>
        {label}
      </div>
      {editing ? (
        <input
          type={type}
          value={editData[key] || ""}
          onChange={(e) =>
            setEditData((prev) => ({ ...prev, [key]: e.target.value }))
          }
          style={inputStyle}
        />
      ) : (
        <div
          className={
            [
              "phone",
              "email",
              "ssn",
              "dob",
              "routing",
              "account",
              "ein",
              "vin",
              "unit_number",
            ].includes(key)
              ? "t-data"
              : "t-body"
          }
          style={{ color: "#111" }}
        >
          {driver[key] || "—"}
        </div>
      )}
    </div>
  );

  const ExpiryBadge = ({ dateStr }) => {
    const status = getExpiryStatus(dateStr);
    if (!status)
      return (
        <span className="t-label" style={{ color: "#9ca3af" }}>
          Not set
        </span>
      );
    return (
      <span
        className="t-badge"
        style={{
          padding: "2px 8px",
          borderRadius: "20px",
          background: status.bg,
          color: status.color,
        }}
      >
        {status.label}
      </span>
    );
  };

  const PanelField = ({ label, children }) => (
    <div>
      <div
        style={{
          fontSize: "10px",
          fontWeight: "500",
          color: "#9ca3af",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          marginBottom: "4px",
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );

  // ── Doc tiles config ──
  const docTiles = [
    {
      key: "cdl",
      label: "CDL",
      subLabel: "Expiration Date",
      value: fmtDateDisplay(driver?.cdl_expiry),
      onFile: !!(driver?.cdl_link_sb || driver?.cdl_link),
      valueColor: (() => {
        const s = getExpiryStatus(driver?.cdl_expiry);
        if (s?.color === "#a32d2d") return "#f87171";
        if (s?.color === "#854f0b") return "#ef9f27";
        return "#e2e8e4";
      })(),
    },
    {
      key: "med",
      label: "Med Card",
      subLabel: "Expiration Date",
      value: fmtDateDisplay(driver?.med_card_expiry),
      onFile: !!(driver?.med_card_link_sb || driver?.med_card_link),
      valueColor: (() => {
        const s = getExpiryStatus(driver?.med_card_expiry);
        if (s?.color === "#a32d2d") return "#f87171";
        if (s?.color === "#854f0b") return "#ef9f27";
        return "#e2e8e4";
      })(),
    },
    {
      key: "mvr",
      label: "MVR",
      subLabel: "Expiration Date",
      value: fmtDateDisplay(driver?.mvr_expiry),
      onFile: !!driver?.mvr_link,
      valueColor: (() => {
        const s = getExpiryStatus(driver?.mvr_expiry);
        if (s?.color === "#a32d2d") return "#f87171";
        if (s?.color === "#854f0b") return "#ef9f27";
        return "#e2e8e4";
      })(),
    },
    {
      key: "ch",
      label: "C/House",
      subLabel: "Expiration Date",
      value: fmtDateDisplay(driver?.clearinghouse_expiry),
      onFile: !!driver?.clearinghouse_link,
      valueColor: (() => {
        const s = getExpiryStatus(driver?.clearinghouse_expiry);
        if (s?.color === "#a32d2d") return "#f87171";
        if (s?.color === "#854f0b") return "#ef9f27";
        return "#e2e8e4";
      })(),
    },
    {
      key: "ssn",
      label: "SSN",
      subLabel: "Social Security Number",
      value: driver?.ssn ? "•••-••-" + String(driver.ssn).slice(-4) : "N/A",
      onFile: !!driver?.ssn_link,
      valueColor: "#e2e8e4",
    },
  ];

  // ── Doc panel content ──
  const DocPanel = () => {
    if (!activeDoc) return null;
    const panelConfigs = {
      cdl: {
        label: "CDL License",
        icon: "🪪",
        onFile: !!(driver?.cdl_link_sb || driver?.cdl_link),
        fileCol: "cdl_link_sb",
        fileUrl: driver?.cdl_link_sb || driver?.cdl_link,
        body: (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "10px",
              }}
            >
              <PanelField label="Driver License #">
                <input
                  style={panelInputStyle}
                  value={docForm.cdl_number || ""}
                  onChange={(e) =>
                    setDocForm((p) => ({ ...p, cdl_number: e.target.value }))
                  }
                  placeholder="e.g. OH12345678"
                />
              </PanelField>
              <PanelField label="Issued State">
                <input
                  style={panelInputStyle}
                  value={docForm.cdl_issued_state || ""}
                  onChange={(e) =>
                    setDocForm((p) => ({
                      ...p,
                      cdl_issued_state: e.target.value,
                    }))
                  }
                  placeholder="e.g. Ohio"
                />
              </PanelField>
              <PanelField label="Class">
                <input
                  style={panelInputStyle}
                  value={docForm.cdl_class || ""}
                  onChange={(e) =>
                    setDocForm((p) => ({ ...p, cdl_class: e.target.value }))
                  }
                  placeholder="e.g. Class A"
                />
              </PanelField>
              <PanelField label="Issue Date">
                <input
                  type="date"
                  style={panelInputStyle}
                  value={docForm.cdl_issue_date || ""}
                  onChange={(e) =>
                    setDocForm((p) => ({
                      ...p,
                      cdl_issue_date: e.target.value,
                    }))
                  }
                />
              </PanelField>
            </div>
            <PanelField label="Expiration Date">
              <input
                type="date"
                style={panelInputStyle}
                value={docForm.cdl_expiry || ""}
                onChange={(e) =>
                  setDocForm((p) => ({ ...p, cdl_expiry: e.target.value }))
                }
              />
            </PanelField>
            <PanelField label="Comments">
              <textarea
                style={{
                  ...panelInputStyle,
                  resize: "vertical",
                  minHeight: "44px",
                }}
                value={docForm.cdl_notes || ""}
                onChange={(e) =>
                  setDocForm((p) => ({ ...p, cdl_notes: e.target.value }))
                }
                placeholder="Add a note..."
              />
            </PanelField>
          </>
        ),
      },
      med: {
        label: "Medical Card",
        icon: "🏥",
        onFile: !!(driver?.med_card_link_sb || driver?.med_card_link),
        fileCol: "med_card_link_sb",
        fileUrl: driver?.med_card_link_sb || driver?.med_card_link,
        body: (
          <>
            <PanelField label="Expiration Date">
              <input
                type="date"
                style={panelInputStyle}
                value={docForm.med_card_expiry || ""}
                onChange={(e) =>
                  setDocForm((p) => ({ ...p, med_card_expiry: e.target.value }))
                }
              />
            </PanelField>
            <PanelField label="Comments">
              <textarea
                style={{
                  ...panelInputStyle,
                  resize: "vertical",
                  minHeight: "44px",
                }}
                value={docForm.med_card_notes || ""}
                onChange={(e) =>
                  setDocForm((p) => ({ ...p, med_card_notes: e.target.value }))
                }
                placeholder="Add a note..."
              />
            </PanelField>
          </>
        ),
      },
      mvr: {
        label: "MVR",
        icon: "📋",
        onFile: !!driver?.mvr_link,
        fileCol: "mvr_link",
        fileUrl: driver?.mvr_link,
        body: (
          <>
            <PanelField label="Last Collected On">
              <input
                type="date"
                style={panelInputStyle}
                value={docForm.mvr_collected_on || ""}
                onChange={(e) =>
                  setDocForm((p) => ({
                    ...p,
                    mvr_collected_on: e.target.value,
                  }))
                }
              />
            </PanelField>
            <PanelField label="Expiration Date">
              <input
                type="date"
                style={panelInputStyle}
                value={docForm.mvr_expiry || ""}
                onChange={(e) =>
                  setDocForm((p) => ({ ...p, mvr_expiry: e.target.value }))
                }
              />
            </PanelField>
            <PanelField label="Comments">
              <textarea
                style={{
                  ...panelInputStyle,
                  resize: "vertical",
                  minHeight: "44px",
                }}
                value={docForm.mvr_notes || ""}
                onChange={(e) =>
                  setDocForm((p) => ({ ...p, mvr_notes: e.target.value }))
                }
                placeholder="Add a note..."
              />
            </PanelField>
          </>
        ),
      },
      ch: {
        label: "Clearinghouse",
        icon: "🏦",
        onFile: !!driver?.clearinghouse_link,
        fileCol: "clearinghouse_link",
        fileUrl: driver?.clearinghouse_link,
        body: (
          <>
            <PanelField label="Last Collected On">
              <input
                type="date"
                style={panelInputStyle}
                value={docForm.clearinghouse_collected_on || ""}
                onChange={(e) =>
                  setDocForm((p) => ({
                    ...p,
                    clearinghouse_collected_on: e.target.value,
                  }))
                }
              />
            </PanelField>
            <PanelField label="Expiration Date">
              <input
                type="date"
                style={panelInputStyle}
                value={docForm.clearinghouse_expiry || ""}
                onChange={(e) =>
                  setDocForm((p) => ({
                    ...p,
                    clearinghouse_expiry: e.target.value,
                  }))
                }
              />
            </PanelField>
            <PanelField label="Comments">
              <textarea
                style={{
                  ...panelInputStyle,
                  resize: "vertical",
                  minHeight: "44px",
                }}
                value={docForm.clearinghouse_notes || ""}
                onChange={(e) =>
                  setDocForm((p) => ({
                    ...p,
                    clearinghouse_notes: e.target.value,
                  }))
                }
                placeholder="Add a note..."
              />
            </PanelField>
          </>
        ),
      },
      ssn: {
        label: "SSN",
        icon: "🔒",
        onFile: !!driver?.ssn_link,
        fileCol: "ssn_link",
        fileUrl: driver?.ssn_link,
        body: (
          <>
            <PanelField label="SSN Number">
              <input
                style={panelInputStyle}
                value={docForm.ssn || ""}
                onChange={(e) =>
                  setDocForm((p) => ({ ...p, ssn: e.target.value }))
                }
                placeholder="•••-••-••••"
              />
            </PanelField>
            <PanelField label="Comments">
              <textarea
                style={{
                  ...panelInputStyle,
                  resize: "vertical",
                  minHeight: "44px",
                }}
                value={docForm.ssn_notes || ""}
                onChange={(e) =>
                  setDocForm((p) => ({ ...p, ssn_notes: e.target.value }))
                }
                placeholder="Add a note..."
              />
            </PanelField>
          </>
        ),
      },
    };

    const cfg = panelConfigs[activeDoc];
    if (!cfg) return null;

    return (
      <div
        style={{
          width: "300px",
          flexShrink: 0,
          background: "#fff",
          borderLeft: "0.5px solid #e8e8e8",
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 57px)",
          position: "sticky",
          top: "57px",
        }}
      >
        {/* Panel header */}
        <div
          style={{
            padding: "12px 16px",
            borderBottom: "0.5px solid #e8e8e8",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "#fff",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div
              style={{
                width: "28px",
                height: "28px",
                background: cfg.onFile ? "#eaf3de" : "#f0f0f0",
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "14px",
              }}
            >
              {cfg.icon}
            </div>
            <div style={{ fontSize: "13px", fontWeight: "500", color: "#111" }}>
              {cfg.label}
            </div>
          </div>
          <span
            style={{
              fontSize: "10px",
              fontWeight: "500",
              padding: "2px 7px",
              borderRadius: "20px",
              background: cfg.onFile ? "#eaf3de" : "#f0f0f0",
              color: cfg.onFile ? "#3b6d11" : "#6b7280",
            }}
          >
            {cfg.onFile ? "✓ On File" : "Not on file"}
          </span>
        </div>

        {/* Panel body */}
        {/* Panel body */}
        <div
          style={{
            overflowY: "auto",
            padding: "10px 12px",
            display: "flex",
            flexDirection: "column",
            gap: "6px",
          }}
        >
          {/* Document image / upload */}
          <div>
            <div
              style={{
                fontSize: "10px",
                fontWeight: "500",
                color: "#9ca3af",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginBottom: "6px",
              }}
            >
              Document
            </div>
            {cfg.fileUrl ? (
              <DocPreview
                url={cfg.fileUrl}
                replacing={uploading}
                onReplace={(e) =>
                  uploadDoc(e.target.files[0], "driver-docs", cfg.fileCol)
                }
                onPreview={(url) => setLightboxUrl(url)}
              />
            ) : (
              <UploadArea
                label={cfg.label}
                uploading={uploading}
                onUpload={(e) =>
                  uploadDoc(e.target.files[0], "driver-docs", cfg.fileCol)
                }
              />
            )}
          </div>
          <div style={{ height: "0.5px", background: "#f0f0f0" }} />
          {cfg.body}
        </div>

        {/* Panel footer — 3 buttons */}
        <div
          style={{
            padding: "12px 16px",
            borderTop: "0.5px solid #e8e8e8",
            display: "flex",
            gap: "6px",
            background: "#fff",
          }}
        >
          <button
            onClick={saveDocPanel}
            disabled={docSaving}
            style={{
              flex: 1,
              background: "#3a9e5f",
              color: "#fff",
              border: "none",
              borderRadius: "7px",
              padding: "7px 0",
              fontSize: "12px",
              fontWeight: "500",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {docSaving ? "Saving..." : "Save"}
          </button>
          <button
            onClick={cancelDocPanel}
            style={{
              background: "#f0f0f0",
              color: "#555",
              border: "none",
              borderRadius: "7px",
              padding: "7px 12px",
              fontSize: "12px",
              fontWeight: "500",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Cancel
          </button>
          <button
            onClick={closeDocPanel}
            style={{
              background: "#fff",
              color: "#9ca3af",
              border: "0.5px solid #e0e0e0",
              borderRadius: "7px",
              padding: "7px 12px",
              fontSize: "12px",
              fontWeight: "500",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  };

  // ── Doc tiles row ──
  const DocTilesRow = () => (
    <div
      style={{
        background: "#1a1e1c",
        borderRadius: "10px",
        padding: "12px",
        marginBottom: "16px",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: "10px",
        }}
      >
        {docTiles.map((tile) => (
          <div
            key={tile.key}
            onClick={() =>
              activeDoc === tile.key ? closeDocPanel() : openDocPanel(tile.key)
            }
            style={{
              background: "#242927",
              borderRadius: "8px",
              padding: "13px 14px",
              border:
                activeDoc === tile.key
                  ? "1.5px solid #3dcc82"
                  : "0.5px solid rgba(255,255,255,0.07)",
              display: "flex",
              flexDirection: "column",
              gap: "7px",
              cursor: "pointer",
              transition: "border-color 0.15s",
            }}
            onMouseEnter={(e) => {
              if (activeDoc !== tile.key)
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
            }}
            onMouseLeave={(e) => {
              if (activeDoc !== tile.key)
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)";
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontSize: "13px",
                fontWeight: "600",
                color: "#e2e8e4",
              }}
            >
              {tile.label}
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="rgba(255,255,255,0.25)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <div
              style={{
                fontSize: "10px",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.35)",
                letterSpacing: "0.05em",
              }}
            >
              {tile.subLabel}
            </div>
            <div
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: tile.valueColor,
                fontFamily: "var(--font-mono, monospace)",
              }}
            >
              {tile.value}
            </div>
            <DocPill onFile={tile.onFile} />
          </div>
        ))}
      </div>
    </div>
  );

  const infoFields = [
    { key: "first_name", label: "First name" },
    { key: "last_name", label: "Last name" },
    { key: "dob", label: "Date of birth" },
    { key: "ssn", label: "SSN" },
    { key: "phone", label: "Phone" },
    { key: "email", label: "Email" },
    { key: "address", label: "Address" },
    { key: "corp_name", label: "Corporation" },
    { key: "ein", label: "EIN" },
    { key: "routing", label: "Routing number" },
    { key: "account", label: "Account number" },
    { key: "pp_device_id", label: "PP Device ID" },
  ];

  const pipelineItems = [
    { label: "Contract", field: "contract_sent", options: ["sent"] },
  ];

  const btnStyle = (bg, color) => ({
    background: bg,
    color,
    fontSize: "12px",
    fontWeight: "500",
    letterSpacing: "-0.01em",
    padding: "6px 14px",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    fontFamily: "inherit",
  });
  const SectionLabel = ({ children }) => (
    <div className="t-label" style={{ marginBottom: "12px", color: "#6b7280" }}>
      {children}
    </div>
  );

  const ChatIdCard = () => (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <SectionLabel>Telegram</SectionLabel>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginBottom: driver.chat_id ? "0" : "10px",
        }}
      >
        <div
          style={{
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            background: driver.chat_id ? "#eaf3de" : "#f0f0f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "16px",
            flexShrink: 0,
          }}
        >
          {driver.chat_id ? "✅" : "💬"}
        </div>
        <div>
          <div
            className="t-label"
            style={{ color: "#6b7280", marginBottom: "2px" }}
          >
            Chat ID
          </div>
          {editing ? (
            <input
              type="text"
              value={editData.chat_id || ""}
              onChange={(e) =>
                setEditData((p) => ({ ...p, chat_id: e.target.value }))
              }
              placeholder="e.g. 198233634"
              style={{ ...inputStyle, width: "160px" }}
            />
          ) : (
            <div
              className="t-data"
              style={{ color: driver.chat_id ? "#111" : "#9ca3af" }}
            >
              {driver.chat_id || "Not connected"}
            </div>
          )}
        </div>
      </div>
      {!driver.chat_id && !editing && (
        <div
          style={{
            background: "#fafafa",
            border: "0.5px solid #e8e8e8",
            borderRadius: "8px",
            padding: "10px 12px",
            marginTop: "10px",
          }}
        >
          <div
            className="t-label"
            style={{ color: "#555", marginBottom: "2px" }}
          >
            Pending Telegram connection
          </div>
          <div className="t-label" style={{ color: "#9ca3af" }}>
            Ask the driver to message{" "}
            <span style={{ color: "#3a9e5f", fontWeight: "500" }}>
              @porso_onboarding_bot
            </span>{" "}
            — their Chat ID will be saved automatically. Or paste it above in
            Edit mode.
          </div>
        </div>
      )}
    </div>
  );

  const SettlementHistoryCard = () => (
    <div
      className="bg-white border border-gray-200 rounded-xl"
      style={{ overflow: "hidden" }}
    >
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "0.5px solid #e8e8e8",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div>
          <div
            className="t-label"
            style={{ color: "#6b7280", marginBottom: "2px" }}
          >
            Settlement history
          </div>
          {loadingStatements ? (
            <div className="t-label" style={{ color: "#9ca3af" }}>
              Loading...
            </div>
          ) : statements.length === 0 ? (
            <div className="t-label" style={{ color: "#9ca3af" }}>
              No statements yet
            </div>
          ) : (
            <div className="t-label" style={{ color: "#9ca3af" }}>
              {statements.length} statement{statements.length !== 1 ? "s" : ""}{" "}
              ·{" "}
              <span style={{ color: "#3b6d11", fontWeight: "500" }}>
                {fmt(totalNetPaid)} net paid
              </span>
            </div>
          )}
        </div>
        <button
          onClick={() => navigate("/settlements")}
          style={{
            background: "#1a1e1c",
            color: "#fff",
            fontSize: "11px",
            fontWeight: "500",
            padding: "5px 10px",
            borderRadius: "8px",
            border: "none",
            cursor: "pointer",
            fontFamily: "inherit",
            whiteSpace: "nowrap",
          }}
        >
          + New
        </button>
      </div>
      {loadingStatements ? (
        <div
          style={{ padding: "20px", textAlign: "center" }}
          className="t-label"
        >
          Loading...
        </div>
      ) : statements.length === 0 ? (
        <div
          style={{ padding: "20px", textAlign: "center" }}
          className="t-label"
        >
          No statements yet
        </div>
      ) : (
        statements.slice(0, 5).map((s, i) => (
          <div
            key={s.id}
            onClick={() => navigate("/settlements")}
            style={{
              padding: "10px 16px",
              borderBottom:
                i < Math.min(statements.length, 5) - 1
                  ? "0.5px solid #f0f0f0"
                  : "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#fafafa")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
          >
            <div>
              <div className="t-name" style={{ fontSize: "12px" }}>
                {fmtDate(s.period_start, { month: "short", day: "numeric" })} –{" "}
                {fmtDate(s.period_end, {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </div>
              <div
                className="t-label"
                style={{ color: "#9ca3af", marginTop: "1px" }}
              >
                {fmt(s.gross_earnings)} gross
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: "500",
                  color: "#3b6d11",
                }}
              >
                {fmt(s.net_pay)}
              </div>
              <span style={{ fontSize: "11px", color: "#3a9e5f" }}>→</span>
            </div>
          </div>
        ))
      )}
      {statements.length > 5 && (
        <div
          onClick={() => navigate("/settlements")}
          style={{
            padding: "10px 16px",
            textAlign: "center",
            fontSize: "12px",
            color: "#3a9e5f",
            cursor: "pointer",
            borderTop: "0.5px solid #f0f0f0",
            fontWeight: "500",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#fafafa")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
        >
          View all {statements.length} statements →
        </div>
      )}
    </div>
  );

  if (loading)
    return (
      <div className="flex min-h-screen">
        {!isMobile && <Sidebar />}
        {isMobile && <MobileHeader title="Driver Profile" />}
        <div
          className="flex-1 flex items-center justify-center t-body"
          style={{ color: "#9ca3af" }}
        >
          Loading...
        </div>
      </div>
    );

  if (!driver)
    return (
      <div className="flex min-h-screen">
        {!isMobile && <Sidebar />}
        {isMobile && <MobileHeader title="Driver Profile" />}
        <div
          className="flex-1 flex items-center justify-center t-body"
          style={{ color: "#9ca3af" }}
        >
          Driver not found
        </div>
      </div>
    );

  return (
    <div className="flex min-h-screen bg-gray-50">
      {!isMobile && <Sidebar />}
      <div
        className="flex-1"
        style={{ minWidth: 0, display: "flex", flexDirection: "column" }}
      >
        {isMobile && (
          <MobileHeader title={`${driver.first_name} ${driver.last_name}`} />
        )}

        {/* Top bar */}
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/drivers")}
              style={{
                fontSize: "12px",
                color: "#9ca3af",
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              ← Back
            </button>
            <span style={{ color: "#e5e7eb" }}>/</span>
            <h1 className="t-name" style={{ color: "#111" }}>
              {driver.first_name} {driver.last_name}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {editing ? (
              <>
                <button
                  onClick={saveEdit}
                  disabled={saving}
                  style={btnStyle("#3a9e5f", "#fff")}
                >
                  {saving ? "Saving..." : "Save changes"}
                </button>
                <button
                  onClick={() => {
                    setEditing(false);
                    setEditData(driver);
                  }}
                  style={btnStyle("#f0f0f0", "#555")}
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={generateContract}
                  style={btnStyle("#185fa5", "#fff")}
                >
                  📄 Generate Contract
                </button>
                <button
                  onClick={() => setEditing(true)}
                  style={btnStyle("#f0f0f0", "#444")}
                >
                  ✏️ Edit
                </button>
                <button
                  onClick={() => setDeleteConfirm(true)}
                  style={btnStyle("#fcebeb", "#a32d2d")}
                >
                  🗑️ Delete
                </button>
              </>
            )}
          </div>
        </div>

        {/* Delete modal */}
        {deleteConfirm && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.45)",
              zIndex: 100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: "12px",
                padding: "24px",
                maxWidth: "360px",
                width: "90%",
              }}
            >
              <div
                className="t-name"
                style={{ marginBottom: "8px", fontSize: "15px" }}
              >
                Delete driver?
              </div>
              <div
                className="t-body"
                style={{ color: "#6b7280", marginBottom: "20px" }}
              >
                Are you sure you want to delete {driver.first_name}{" "}
                {driver.last_name}? This cannot be undone.
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={deleteDriver}
                  style={btnStyle("#a32d2d", "#fff")}
                >
                  Yes, delete
                </button>
                <button
                  onClick={() => setDeleteConfirm(false)}
                  style={btnStyle("#f0f0f0", "#555")}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main content + doc panel side by side */}
        <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: isMobile ? "12px" : "24px",
            }}
          >
            {/* Doc tiles — always shown on desktop */}
            {!isMobile && <DocTilesRow />}

            {isMobile ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <DocTilesRow />
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      marginBottom: "16px",
                    }}
                  >
                    <div
                      style={{
                        width: "44px",
                        height: "44px",
                        borderRadius: "50%",
                        background: "#eaf3de",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#3a9e5f",
                        flexShrink: 0,
                      }}
                    >
                      {driver.first_name?.[0]}
                      {driver.last_name?.[0]}
                    </div>
                    <div>
                      <div className="t-name" style={{ fontSize: "14px" }}>
                        {driver.first_name} {driver.last_name}
                      </div>
                      <div className="t-data" style={{ color: "#9ca3af" }}>
                        Applied{" "}
                        {new Date(driver.submitted_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "12px",
                    }}
                  >
                    {infoFields.map((f) => editField(f.key, f.label))}
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <SectionLabel>Assigned truck</SectionLabel>
                  {assignedTruck ? (
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          padding: "10px",
                          background: "#fafafa",
                          borderRadius: "8px",
                          border: "0.5px solid #e8e8e8",
                          marginBottom: "12px",
                        }}
                      >
                        <div
                          style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "8px",
                            background: "#e6f4ed",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "10px",
                            fontWeight: "600",
                            color: "#238b58",
                            flexShrink: 0,
                          }}
                        >
                          #{assignedTruck.unit_number}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div className="t-name" style={{ fontSize: "13px" }}>
                            {assignedTruck.make} {assignedTruck.year}
                          </div>
                          <div
                            className="t-label"
                            style={{ color: "#9ca3af", marginTop: "2px" }}
                          >
                            {assignedTruck.license_plate} ·{" "}
                            {assignedTruck.state}
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: "10px",
                        }}
                      >
                        {[
                          { label: "Unit #", value: assignedTruck.unit_number },
                          { label: "VIN", value: assignedTruck.vin },
                          { label: "Year", value: assignedTruck.year },
                          { label: "Make", value: assignedTruck.make },
                        ].map(({ label, value }) => (
                          <div key={label}>
                            <div
                              className="t-label"
                              style={{ marginBottom: "4px" }}
                            >
                              {label}
                            </div>
                            <div className="t-data" style={{ color: "#111" }}>
                              {value || "—"}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "12px",
                        background: "#fafafa",
                        borderRadius: "8px",
                        border: "0.5px solid #e8e8e8",
                      }}
                    >
                      <div
                        style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "8px",
                          background: "#f0f0f0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          fontSize: "16px",
                        }}
                      >
                        🚛
                      </div>
                      <div>
                        <div className="t-name" style={{ fontSize: "12px" }}>
                          No truck assigned
                        </div>
                        <div
                          className="t-label"
                          style={{ color: "#9ca3af", marginTop: "2px" }}
                        >
                          Assign from Fleet Overview
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <ChatIdCard />
                <SettlementHistoryCard />
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <SectionLabel>Pipeline status</SectionLabel>
                  {pipelineItems.map((item) => (
                    <div key={item.field} style={{ marginBottom: "12px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: "6px",
                        }}
                      >
                        <span className="t-label" style={{ color: "#6b7280" }}>
                          {item.label}
                        </span>
                        <span className={getBadge(driver[item.field])}>
                          {driver[item.field]}
                        </span>
                      </div>
                      <div style={{ display: "flex", gap: "6px" }}>
                        {item.options.map((option) => (
                          <button
                            key={option}
                            onClick={() => updateStatus(item.field, option)}
                            disabled={
                              updating === item.field ||
                              driver[item.field] === option
                            }
                            className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                              option === "pass" || option === "sent"
                                ? "border-green-200 text-green-700 hover:bg-green-50"
                                : "border-red-200 text-red-700 hover:bg-red-50"
                            } disabled:opacity-40`}
                          >
                            {updating === item.field ? "..." : option}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <SectionLabel>Internal notes</SectionLabel>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Add notes about this driver..."
                    style={{
                      width: "100%",
                      border: "0.5px solid #e0e0e0",
                      borderRadius: "8px",
                      padding: "8px 12px",
                      fontSize: "13px",
                      outline: "none",
                      resize: "vertical",
                      minHeight: "80px",
                      boxSizing: "border-box",
                      color: "#111",
                      fontFamily: "inherit",
                    }}
                  />
                  <button
                    onClick={saveNote}
                    disabled={savingNote}
                    style={{ ...btnStyle("#3a9e5f", "#fff"), marginTop: "8px" }}
                  >
                    {savingNote ? "Saving..." : "Save note"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="col-span-2 space-y-4">
                  <div className="bg-white border border-gray-200 rounded-xl p-5">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        marginBottom: "20px",
                      }}
                    >
                      <div
                        style={{
                          width: "44px",
                          height: "44px",
                          borderRadius: "50%",
                          background: "#eaf3de",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "16px",
                          fontWeight: "600",
                          color: "#3a9e5f",
                          flexShrink: 0,
                        }}
                      >
                        {driver.first_name?.[0]}
                        {driver.last_name?.[0]}
                      </div>
                      <div>
                        <div className="t-name" style={{ fontSize: "14px" }}>
                          {driver.first_name} {driver.last_name}
                        </div>
                        <div className="t-data" style={{ color: "#9ca3af" }}>
                          Applied{" "}
                          {new Date(driver.submitted_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "16px",
                      }}
                    >
                      {infoFields.map((f) => editField(f.key, f.label))}
                    </div>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-xl p-5">
                    <SectionLabel>Assigned truck</SectionLabel>
                    {assignedTruck ? (
                      <div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            padding: "12px",
                            background: "#fafafa",
                            borderRadius: "8px",
                            border: "0.5px solid #e8e8e8",
                            marginBottom: "14px",
                          }}
                        >
                          <div
                            style={{
                              width: "40px",
                              height: "40px",
                              borderRadius: "8px",
                              background: "#e6f4ed",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "11px",
                              fontWeight: "600",
                              color: "#238b58",
                              flexShrink: 0,
                            }}
                          >
                            #{assignedTruck.unit_number}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div
                              className="t-name"
                              style={{ fontSize: "13px" }}
                            >
                              {assignedTruck.make} {assignedTruck.year}
                            </div>
                            <div
                              className="t-label"
                              style={{ color: "#9ca3af", marginTop: "2px" }}
                            >
                              {assignedTruck.license_plate} ·{" "}
                              {assignedTruck.state} · {assignedTruck.ownership}
                            </div>
                          </div>
                          <button
                            onClick={() => (window.location.href = "/vehicles")}
                            style={{
                              fontSize: "11px",
                              color: "#238b58",
                              fontWeight: "500",
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              fontFamily: "inherit",
                            }}
                          >
                            View in Fleet →
                          </button>
                        </div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "12px",
                          }}
                        >
                          {[
                            {
                              label: "Unit #",
                              value: assignedTruck.unit_number,
                            },
                            { label: "VIN", value: assignedTruck.vin },
                            { label: "Year", value: assignedTruck.year },
                            { label: "Make", value: assignedTruck.make },
                            {
                              label: "License plate",
                              value: assignedTruck.license_plate,
                            },
                            { label: "State", value: assignedTruck.state },
                          ].map(({ label, value }) => (
                            <div key={label}>
                              <div
                                className="t-label"
                                style={{ marginBottom: "4px" }}
                              >
                                {label}
                              </div>
                              <div className="t-data" style={{ color: "#111" }}>
                                {value || "—"}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          padding: "14px",
                          background: "#fafafa",
                          borderRadius: "8px",
                          border: "0.5px solid #e8e8e8",
                        }}
                      >
                        <div
                          style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "8px",
                            background: "#f0f0f0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            fontSize: "18px",
                          }}
                        >
                          🚛
                        </div>
                        <div>
                          <div className="t-name" style={{ fontSize: "13px" }}>
                            No truck assigned
                          </div>
                          <div
                            className="t-label"
                            style={{ color: "#9ca3af", marginTop: "2px" }}
                          >
                            Assign a truck from Fleet Overview to populate this
                            section
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="bg-white border border-gray-200 rounded-xl p-5">
                    <SectionLabel>Document expiry dates</SectionLabel>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "16px",
                      }}
                    >
                      <div>
                        <div
                          className="t-label"
                          style={{ marginBottom: "4px" }}
                        >
                          CDL expiry date
                        </div>
                        {editing ? (
                          <input
                            type="date"
                            value={editData.cdl_expiry || ""}
                            onChange={(e) =>
                              setEditData((p) => ({
                                ...p,
                                cdl_expiry: e.target.value,
                              }))
                            }
                            style={inputStyle}
                          />
                        ) : (
                          <div>
                            <div
                              className="t-data"
                              style={{ color: "#111", marginBottom: "4px" }}
                            >
                              {driver.cdl_expiry
                                ? new Date(
                                    driver.cdl_expiry
                                  ).toLocaleDateString()
                                : "—"}
                            </div>
                            <ExpiryBadge dateStr={driver.cdl_expiry} />
                          </div>
                        )}
                      </div>
                      <div>
                        <div
                          className="t-label"
                          style={{ marginBottom: "4px" }}
                        >
                          Medical card expiry date
                        </div>
                        {editing ? (
                          <input
                            type="date"
                            value={editData.med_card_expiry || ""}
                            onChange={(e) =>
                              setEditData((p) => ({
                                ...p,
                                med_card_expiry: e.target.value,
                              }))
                            }
                            style={inputStyle}
                          />
                        ) : (
                          <div>
                            <div
                              className="t-data"
                              style={{ color: "#111", marginBottom: "4px" }}
                            >
                              {driver.med_card_expiry
                                ? new Date(
                                    driver.med_card_expiry
                                  ).toLocaleDateString()
                                : "—"}
                            </div>
                            <ExpiryBadge dateStr={driver.med_card_expiry} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-xl p-5">
                    <SectionLabel>Internal notes</SectionLabel>
                    <textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Add internal notes about this driver..."
                      style={{
                        width: "100%",
                        border: "0.5px solid #e0e0e0",
                        borderRadius: "8px",
                        padding: "8px 12px",
                        fontSize: "13px",
                        outline: "none",
                        resize: "vertical",
                        minHeight: "100px",
                        boxSizing: "border-box",
                        color: "#111",
                        fontFamily: "inherit",
                      }}
                    />
                    <button
                      onClick={saveNote}
                      disabled={savingNote}
                      style={{
                        ...btnStyle("#3a9e5f", "#fff"),
                        marginTop: "8px",
                      }}
                    >
                      {savingNote ? "Saving..." : "Save note"}
                    </button>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <SectionLabel>Pipeline status</SectionLabel>
                    {pipelineItems.map((item) => (
                      <div key={item.field} style={{ marginBottom: "12px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "6px",
                          }}
                        >
                          <span
                            className="t-label"
                            style={{ color: "#6b7280" }}
                          >
                            {item.label}
                          </span>
                          <span className={getBadge(driver[item.field])}>
                            {driver[item.field]}
                          </span>
                        </div>
                        <div style={{ display: "flex", gap: "6px" }}>
                          {item.options.map((option) => (
                            <button
                              key={option}
                              onClick={() => updateStatus(item.field, option)}
                              disabled={
                                updating === item.field ||
                                driver[item.field] === option
                              }
                              className={`text-xs px-2.5 py-1 rounded-lg border transition-colors ${
                                option === "pass" || option === "sent"
                                  ? "border-green-200 text-green-700 hover:bg-green-50"
                                  : "border-red-200 text-red-700 hover:bg-red-50"
                              } disabled:opacity-40`}
                            >
                              {updating === item.field ? "..." : option}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <ChatIdCard />
                  <SettlementHistoryCard />
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <SectionLabel>Documents</SectionLabel>
                    {driver.cdl_link && (
                      <a
                        href={driver.cdl_link}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          fontSize: "12px",
                          color: "#3a9e5f",
                          textDecoration: "none",
                          marginBottom: "8px",
                        }}
                      >
                        📄 View CDL License
                      </a>
                    )}
                    {driver.med_card_link && (
                      <a
                        href={driver.med_card_link}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          fontSize: "12px",
                          color: "#3a9e5f",
                          textDecoration: "none",
                        }}
                      >
                        🏥 View Medical Card
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Doc detail panel */}
          {activeDoc && !isMobile && <DocPanel />}
        </div>

        {/* Lightbox */}
        {lightboxUrl && (
          <div
            onClick={() => setLightboxUrl(null)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.9)",
              zIndex: 2000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
              cursor: "zoom-out",
            }}
          >
            <button
              onClick={() => setLightboxUrl(null)}
              style={{
                position: "absolute",
                top: "16px",
                right: "16px",
                background: "rgba(255,255,255,0.15)",
                border: "none",
                borderRadius: "50%",
                width: "36px",
                height: "36px",
                cursor: "pointer",
                fontSize: "20px",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ×
            </button>
            <img
              src={lightboxUrl}
              alt="Document preview"
              style={{
                maxWidth: "100%",
                maxHeight: "90vh",
                objectFit: "contain",
                borderRadius: "8px",
                boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {/* Mobile doc panel as bottom sheet */}
        {activeDoc && isMobile && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.4)",
              zIndex: 900,
              display: "flex",
              alignItems: "flex-end",
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) closeDocPanel();
            }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: "16px 16px 0 0",
                width: "100%",
                maxHeight: "85vh",
                overflowY: "auto",
              }}
            >
              <DocPanel />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
